import * as React from "react";
import { inject, observer } from "mobx-react";
import { CarSubLOBs } from "src/stores/wizard/state/typings";
import { WizardCarPWAProps, WizardCarPWAState, WizardCarPWAFTNodeOptions } from "./typings";
import { WizardCarPWAView } from "./views/WizardCarPWAView";
import { WizardCarPWAHotwireView } from "./views/WizardCarPWAHotwireView";

import { Experiment, isVariantEnabled } from "../../utility/experiment";

import { WizardCarPWAViews } from "./utils/WizardCarPWAViews";
import { normalizeOptionsData } from "src/components/flexFramework/FlexTemplateNode";
import { isAddCSRFilter } from "components/flexComponents/WizardCarPWA/utils/isAddCSRFilter";

const getComponentFromView = (view?: string) => {
  switch (view) {
    case WizardCarPWAViews.HOTWIRE:
      return WizardCarPWAHotwireView;
    default:
      return WizardCarPWAView;
  }
};

@inject("wizardState", "routeStore", "compositionStore", "flexModuleModelStore", "page")
@observer
export class WizardCarPWA extends React.Component<WizardCarPWAProps, WizardCarPWAState> {
  private options: WizardCarPWAFTNodeOptions;

  constructor(props: WizardCarPWAProps) {
    super(props);
    this.state = {
      isDiffDropOff: false,
      isDriverAgeInRange: true,
      isAARPChecked: false,
    };

    const { wizardState, flexTemplateNode, flexModuleModelStore, context } = this.props;
    const { carWizardState, globalWizardState } = wizardState;

    const model = flexModuleModelStore.getModel(flexTemplateNode.id);
    this.options = normalizeOptionsData(flexTemplateNode, model) as WizardCarPWAFTNodeOptions;

    carWizardState.overrideConfig(() => {
      carWizardState.config.elementId = flexTemplateNode.id;
      carWizardState.config.heading.fixedTitle = this.options.fixedLobTitle;
      carWizardState.config.lastSearch.useLastSearchForSingleLOB = this.options.useLastSearchForSingleLOB;
      carWizardState.config.heading.lobSubtitleBreakPoint = this.options.lobSubtitleBreakPoint;
      carWizardState.config.heading.hideLobTitle = this.options.hideLobTitle;
      carWizardState.config.includeGT = this.options.includeGT ?? carWizardState.config.includeGT;
      carWizardState.config.disableVendorCouponCode =
        this.options.disableVendorCouponCode ?? carWizardState.config.disableVendorCouponCode;
      carWizardState.config.iHaveDiscount = carWizardState.config.disableVendorCouponCode
        ? "wizard.car.having.brand.title"
        : "wizard.car.having.discount";
      if (this.options.titleFromPageHeader) {
        carWizardState.config.heading.differentiatedTitles = false;
      }
      const addGoogleFallback = (essFeatures: string): string => {
        if (essFeatures.indexOf("google") < 0) {
          return `${essFeatures}|google`;
        }

        return essFeatures;
      };
      const essFeaturesDest = carWizardState.config.location.destination.essAdapterConfig.features;
      const essFeaturesOrigin = carWizardState.config.location.origin.essAdapterConfig.features;
      carWizardState.config.location.destination.essAdapterConfig.features = addGoogleFallback(essFeaturesDest);
      carWizardState.config.location.origin.essAdapterConfig.features = addGoogleFallback(essFeaturesOrigin);

      if (isVariantEnabled(context, Experiment.Cars_Lift30DaysRestrictionOnEMEA)) {
        carWizardState.config.date.maxDaysRange = 329;
      }

      if (context.searchContext.pageType === "Car-Rental-Supplier-Locations" && isAddCSRFilter(this.options)) {
        const { carSupplierLocation, carSupplier } = context.searchContext;
        carWizardState.location.origin.value = `${carSupplier.name} - ${carSupplierLocation.address.firstAddressLine}, ${carSupplierLocation.address.city}, ${carSupplierLocation.address.province} ${carSupplierLocation.address.postalCode}`;
      }
    });

    if (globalWizardState.shouldUpdateCookie) {
      carWizardState.updateStateFromCookie();
    }
    carWizardState.updateDateFromConfig();
  }

  public componentDidMount() {
    const { wizardState } = this.props;
    const { carWizardState } = wizardState;

    if (this.isGTFlexModule(this.options.flexComponentName)) {
      carWizardState.updateSubLOBState(CarSubLOBs.GROUND_TRANSPORTATION);
    } else {
      this.setState(() => ({
        isDiffDropOff: wizardState.carWizardState.isDifferentDropOff,
      }));
      carWizardState.updateSubLOBState(CarSubLOBs.RENTAL);
    }
  }

  /* Making sure the subLob does not reset to car for wizard-gt-pwa module (gtOnly module) when the component updates */
  public componentDidUpdate() {
    const { wizardState } = this.props;

    if (this.isGTFlexModule(this.options.flexComponentName)) {
      wizardState.carWizardState.updateSubLOBState(CarSubLOBs.GROUND_TRANSPORTATION);
    }
  }

  private isGTFlexModule = (flexModuleName: string) => flexModuleName === "wizard-gt-pwa";

  private setSelectedTabOptions = (selectedTab: CarSubLOBs) => {
    const { wizardState } = this.props;

    wizardState.carWizardState.updateSubLOBState(selectedTab);
  };

  private toggleDifferentDropOff = () => {
    const { wizardState } = this.props;

    this.setState(({ isDiffDropOff }) => ({
      isDiffDropOff: !isDiffDropOff,
    }));
    wizardState.carWizardState.toggleIsDifferentDropOff();
  };

  private setIsDifferentDropOff = (differentDropOff: boolean) => {
    const { wizardState } = this.props;

    this.setState(() => ({
      isDiffDropOff: differentDropOff,
    }));

    wizardState.carWizardState.setIsDifferentDropoff(differentDropOff);
  };

  private toggleDriverAgeInRange = () => {
    this.setState(({ isDriverAgeInRange }) => ({
      isDriverAgeInRange: !isDriverAgeInRange,
    }));
  };

  private onAARPChange = () => {
    this.setState(({ isAARPChecked }) => ({
      isAARPChecked: !isAARPChecked,
    }));
  };

  private toggleBookRoundtrip = () => {
    const { wizardState } = this.props;

    wizardState.carWizardState.toggleBookRoundtrip();
  };

  public onTabOptionSelected = (selectedTab: CarSubLOBs) => {
    this.setSelectedTabOptions(selectedTab);
  };

  public onDifferentDropOffSelected = () => this.toggleDifferentDropOff();

  public onDriverAgeInRange = () => this.toggleDriverAgeInRange();

  private setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
    const { wizardInputsArray } = this.props.wizardState.carWizardState;
    wizardInputsArray.push(ref);
    if (secondRef) {
      wizardInputsArray.push(secondRef);
    }
  };

  private getLocError = (isLink: boolean) => {
    const {
      carWizardState,
      carWizardState: { config: carConfig },
    } = this.props.wizardState;

    if (!isLink) {
      return carWizardState.moreThanOneError()
        ? carConfig.form.submitButton.locMultipleWarningsToken!
        : carConfig.form.submitButton.locWarningToken!;
    }

    return carWizardState.moreThanOneError()
      ? carConfig.form.submitButton.locMultipleWarningsLinkToken
      : carConfig.form.submitButton.locWarningLinkToken;
  };

  public render() {
    const { isDiffDropOff, isDriverAgeInRange, isAARPChecked } = this.state;
    const { wizardState, compositionStore } = this.props;
    const { showPageHeading, titleFromPageHeader } = this.options;

    const ViewComponent = getComponentFromView(this.options.flexComponentView);

    return (
      <ViewComponent
        {...this.props}
        onTabOptionSelected={this.onTabOptionSelected}
        toggleBookRoundtrip={this.toggleBookRoundtrip}
        onDifferentDropOffSelected={this.onDifferentDropOffSelected}
        setIsDifferentDropOff={this.setIsDifferentDropOff}
        onDriverAgeInRange={this.onDriverAgeInRange}
        onAARPChange={this.onAARPChange}
        isDiffDropOff={isDiffDropOff}
        isDriverAgeInRange={isDriverAgeInRange}
        isAARPChecked={isAARPChecked}
        showGTOnly={this.isGTFlexModule(this.options.flexComponentName)}
        pageHeading={
          ((showPageHeading || titleFromPageHeader) && compositionStore?.composition?.pageHeading) || undefined
        }
        selectedTab={wizardState.carWizardState.subLOBState}
        getLocError={this.getLocError}
        setInputsRefs={this.setInputsRefs}
      />
    );
  }
}

export default WizardCarPWA;
